import React from 'react';
import { zetaPfs } from '../../data/caprelsa';
const PURPLE = '#3d2785';
const GOLD = '#d6a530';
const MONO = 'IBM Plex Mono, monospace';
const X0 = 34;
const X1 = 310;
const Y0 = 14;
const Y1 = 168;
const x = (t: number) => X0 + t / zetaPfs.axisMax * (X1 - X0);
const y = (s: number) => Y1 - s * (Y1 - Y0);

/** Schematic step curve that crosses 50% at the reported median. */
function stepPath(median: number) {
  let d = `M${x(0)} ${y(1)}`;
  for (let t = 1.5; t <= zetaPfs.axisMax; t += 1.5) {
    const s = Math.pow(0.5, t / median);
    d += ` H${x(t).toFixed(1)} V${y(s).toFixed(1)}`;
  }
  return d;
}
const CAPRELSA_PATH = stepPath(zetaPfs.caprelsa);
const PLACEBO_PATH = stepPath(zetaPfs.placebo);

/** Kaplan–Meier-style PFS visual (caprelsa.com efficacy page style), revealed up to `month`. */
export function PfsCurve({
  month
}: {
  month: number;
}) {
  const clipW = x(Math.min(month, zetaPfs.axisMax)) - X0 + 2;
  const showPlacebo = month >= zetaPfs.placebo;
  const showCaprelsa = month >= zetaPfs.caprelsa;
  return <svg viewBox="0 0 320 196" className="h-full w-full" role="img" aria-label={`Schematic progression-free survival curves. Median PFS ${zetaPfs.caprelsa} months (predicted) with Caprelsa versus ${zetaPfs.placebo} months with placebo.`} data-id="element-575" data-mp="14awl0cb55274">
      <defs data-id="element-576" data-mp="ubnu5sbko3oz">
        <clipPath id="pfs-reveal" data-id="element-577" data-mp="zcldi88bj3s2">
          <rect x={X0} y={0} width={Math.max(0, clipW)} height={196} data-id="element-578" data-mp="zdmyb4f4z4mz" />
        </clipPath>
      </defs>

      {/* Grid + axes */}
      {[0, 0.25, 0.5, 0.75, 1].map(s => <g key={s} data-id="element-579" data-mp="1s10s4tnjfk2v">
          <line x1={X0} x2={X1} y1={y(s)} y2={y(s)} stroke={PURPLE} strokeOpacity={s === 0 ? 0.35 : 0.08} data-id="element-580" data-mp="1s07q25zixir2" />
          <text x={X0 - 6} y={y(s) + 3} textAnchor="end" fill="#6b6680" fontSize="7.5" fontFamily={MONO} data-id="element-581" data-mp="1e0pwldba9hbd">
            {s.toFixed(s === 0 || s === 1 ? 0 : 2).replace('0.', '.')}
          </text>
        </g>)}
      <line x1={X0} x2={X0} y1={Y0} y2={Y1} stroke={PURPLE} strokeOpacity="0.35" data-id="element-582" data-mp="2krj8x92o94c" />
      {[0, 6, 12, 18, 24, 30, 36].map(t => <text key={t} x={x(t)} y={Y1 + 11} textAnchor="middle" fill="#6b6680" fontSize="7.5" fontFamily={MONO} data-id="element-583" data-mp="6ll8bty93tyt">
          {t}
        </text>)}
      <text x={(X0 + X1) / 2} y={194} textAnchor="middle" fill="#6b6680" fontSize="7" fontFamily={MONO} letterSpacing="0.8" data-id="element-584" data-mp="24cjl7l6kfmlr">
        MONTHS
      </text>

      {/* Curves, revealed by the time cursor */}
      <g clipPath="url(#pfs-reveal)" data-id="element-585" data-mp="1ukxbxudsfxln">
        <path d={PLACEBO_PATH} fill="none" stroke={GOLD} strokeWidth="2.2" data-id="element-586" data-mp="1ujum7ps7c17b" />
        <path d={CAPRELSA_PATH} fill="none" stroke={PURPLE} strokeWidth="2.6" data-id="element-587" data-mp="1qles71xpdske" />
      </g>

      {/* Median markers */}
      {(showPlacebo || showCaprelsa) && <line x1={X0} x2={x(showCaprelsa ? zetaPfs.caprelsa : zetaPfs.placebo)} y1={y(0.5)} y2={y(0.5)} stroke={PURPLE} strokeOpacity="0.4" strokeDasharray="3 3" data-id="element-588" data-mp="1gl4ezhbo4l58" />}
      {showPlacebo && <g data-id="element-589" data-mp="3b06ymye1d908">
          <line x1={x(zetaPfs.placebo)} x2={x(zetaPfs.placebo)} y1={y(0.5)} y2={Y1} stroke={GOLD} strokeDasharray="3 3" data-id="element-590" data-mp="3ayvwt445ibbr" />
          <circle cx={x(zetaPfs.placebo)} cy={y(0.5)} r="3.5" fill={GOLD} data-id="element-591" data-mp="361dnvdq4vw3r" />
          <text x={x(zetaPfs.placebo) - 4} y={y(0.5) + 16} textAnchor="end" fill="#a07a17" fontSize="10" fontWeight="700" fontFamily="Inter, sans-serif" data-id="element-592" data-mp="3egtf0ne4xgqi">
            {zetaPfs.placebo} mo
          </text>
        </g>}
      {showCaprelsa && <g data-id="element-593" data-mp="3ncivci0q7kx1">
          <line x1={x(zetaPfs.caprelsa)} x2={x(zetaPfs.caprelsa)} y1={y(0.5)} y2={Y1} stroke={PURPLE} strokeDasharray="3 3" data-id="element-594" data-mp="3s8zsu2ttfbms" />
          <circle cx={x(zetaPfs.caprelsa)} cy={y(0.5)} r="4" fill={PURPLE} data-id="element-595" data-mp="2vjs7ovx3z4je" />
          <text x={x(zetaPfs.caprelsa)} y={y(0.5) - 9} textAnchor="middle" fill={PURPLE} fontSize="11" fontWeight="800" fontFamily="Inter, sans-serif" data-id="element-596" data-mp="2zkc0xoek9o09">
            {zetaPfs.caprelsa} mo*
          </text>
        </g>}

      {/* Time cursor */}
      {month > 0 && month < zetaPfs.axisMax && <line x1={x(month)} x2={x(month)} y1={Y0} y2={Y1} stroke={PURPLE} strokeOpacity="0.25" data-id="element-597" data-mp="jtpsp09hkghn" />}
    </svg>;
}